import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile } from "../components/SubsWall";
import "./DashboardV5.css";

interface ApiSupporter {
  name: string;
  monthly_cents: number;
  created_at: number;
  months_subscribed: number;
  fade_started_at: number | null;
}

interface ApiResp {
  signed_in: boolean;
  user?: { handle: string; display_name: string; avatar_url: string | null; is_creator: boolean };
  kpis?: {
    mrr_cents: number;
    take_home_cents: number;
    platform_fee_cents: number;
    supporter_count: number;
    new_this_month: number;
    cancels_this_month: number;
    lifetime_cents: number;
  };
  supporters?: ApiSupporter[];
}

function centsToDollars(c: number): number { return Math.round(c / 100); }
function monthYear(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleString("en-US", { month: "short", year: "2-digit" }).replace(" ", " '");
}
function tierForMonthly(monthly: number): string {
  if (monthly >= 250) return "Architect";
  if (monthly >= 100) return "Founder";
  if (monthly >= 50)  return "Benefactor";
  if (monthly >= 25)  return "Patron";
  if (monthly >= 10)  return "Member";
  return "Supporter";
}

// Mock data shown to anonymous visitors so /dashboard still looks designed.
const MOCK = {
  display_name: "Demo",
  handle: "demo",
  supporters: [
    { name: "James Lockwood", started: "Jun '23", monthly: 250, total: 9000, tier: "Architect" },
    { name: "Helena Voss",    started: "Dec '23", monthly: 100, total: 3000, tier: "Founder" },
    { name: "M.J. Asante",    started: "Apr '24", monthly:  50, total: 1300, tier: "Benefactor" },
    { name: "Dr. Owen Hayes", started: "May '25", monthly:  25, total:  325, tier: "Patron" },
    { name: "Camille Joubert",started: "Jun '25", monthly:  25, total:  300, tier: "Patron" },
    { name: "Hiro Tanaka",    started: "Aug '25", monthly:  10, total:  100, tier: "Member" },
    { name: "Lena R.",        started: "Aug '25", monthly:  10, total:  100, tier: "Member" },
    { name: "Sarah K.",       started: "Sep '25", monthly:   5, total:   45, tier: "Supporter" },
    { name: "Marcus T.",      started: "Sep '25", monthly:   5, total:   45, tier: "Supporter" },
    { name: "Priya N.",       started: "Oct '25", monthly:   5, total:   40, tier: "Supporter" },
  ],
};

export default function DashboardV5(): JSX.Element {
  const [live, setLive] = useState<ApiResp | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/me/dashboard", { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: ApiResp | null) => { if (!cancelled && j) setLive(j); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Build a unified view that always renders something nice.
  const useLive = !!(live && live.signed_in && live.user && live.kpis);
  const displayName = useLive ? live!.user!.display_name : MOCK.display_name;
  const handle      = useLive ? live!.user!.handle       : MOCK.handle;

  const supporters = useLive
    ? (live!.supporters ?? []).map((s) => ({
        name: s.name,
        started: monthYear(s.created_at),
        monthly: centsToDollars(s.monthly_cents),
        total: Math.max(centsToDollars(s.monthly_cents), centsToDollars(s.monthly_cents * Math.max(1, s.months_subscribed))),
        tier: tierForMonthly(centsToDollars(s.monthly_cents)),
      }))
    : MOCK.supporters;

  const mrr = useLive ? centsToDollars(live!.kpis!.mrr_cents) : supporters.reduce((s, x) => s + x.monthly, 0);
  const fee = useLive ? centsToDollars(live!.kpis!.platform_fee_cents) : Math.round(mrr * 0.049);
  const takeHome = useLive ? centsToDollars(live!.kpis!.take_home_cents) : mrr - fee;
  const totalLifetime = useLive
    ? centsToDollars(live!.kpis!.lifetime_cents)
    : supporters.reduce((s, x) => s + x.total, 0);
  const supCount = useLive ? live!.kpis!.supporter_count : supporters.length;
  const newThisMonth = useLive ? live!.kpis!.new_this_month : 2;
  const cancelsThisMonth = useLive ? live!.kpis!.cancels_this_month : 0;

  return (
    <div className="hy-page dbg5">
      <header className="dbg5-nav hy-container">
        <Link to="/" className="dbg5-brand">
          <span className="dbg5-brand-glyph"></span> SubsWall
        </Link>
        <Link to={`/${handle}`} className="hy-btn hy-btn-ghost hy-btn-sm">View my public wall →</Link>
      </header>

      <main className="dbg5-main hy-container">
        <div className="dbg5-head">
          <span className="hy-sticker">your dashboard</span>
          <h1 className="hy-display dbg5-h1">
            Welcome back, <span className="dbg5-h1-script">{displayName}</span>.
          </h1>
        </div>

        <section className="dbg5-kpis">
          <div className="hy-card-yellow dbg5-kpi">
            <div className="dbg5-kpi-lbl">Monthly recurring</div>
            <div className="hy-mono dbg5-kpi-num">${mrr.toLocaleString()}</div>
            <div className="dbg5-kpi-sub">${takeHome.toLocaleString()} to you · ${fee} fee</div>
          </div>
          <div className="hy-card dbg5-kpi">
            <div className="dbg5-kpi-lbl">Supporters</div>
            <div className="hy-mono dbg5-kpi-num">{supCount}</div>
            <div className="dbg5-kpi-sub">+{newThisMonth} this month · {cancelsThisMonth} cancellation{cancelsThisMonth === 1 ? "" : "s"}</div>
          </div>
          <div className="hy-card dbg5-kpi">
            <div className="dbg5-kpi-lbl">Lifetime</div>
            <div className="hy-mono dbg5-kpi-num">${totalLifetime.toLocaleString()}</div>
            <div className="dbg5-kpi-sub">All-time contributed</div>
          </div>
          <div className="hy-card dbg5-kpi dbg5-kpi-payout">
            <div className="dbg5-kpi-lbl">Next payout</div>
            <div className="hy-mono dbg5-kpi-num">${takeHome.toLocaleString()}</div>
            <div className="dbg5-kpi-sub">Direct to your bank · 1st of next month</div>
          </div>
        </section>

        <section className="dbg5-section">
          <div className="dbg5-section-head">
            <h2 className="hy-display dbg5-h2">Your supporters</h2>
            <div className="dbg5-tools">
              <input type="search" placeholder="Search…" className="dbg5-search" />
              <button className="hy-btn hy-btn-ghost hy-btn-sm">Export CSV</button>
            </div>
          </div>
          <div className="hy-card dbg5-table-card">
            <table className="dbg5-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Since</th>
                  <th className="dbg5-num">Monthly</th>
                  <th className="dbg5-num">Lifetime</th>
                  <th>Tier</th>
                </tr>
              </thead>
              <tbody>
                {supporters.length === 0 ? (
                  <tr><td colSpan={5} className="dbg5-empty">
                    No supporters yet. Share <strong>subswall.com/{handle}</strong> to get your first tile.
                  </td></tr>
                ) : supporters.map((s) => (
                  <tr key={s.name}>
                    <td>{s.name}</td>
                    <td className="dbg5-mute hy-mono">{s.started}</td>
                    <td className="dbg5-num hy-mono">${s.monthly}</td>
                    <td className="dbg5-num hy-mono">${s.total.toLocaleString()}</td>
                    <td><span className={`dbg5-tier dbg5-t-${s.tier.toLowerCase()}`}>{s.tier}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="dbg5-section">
          <div className="dbg5-section-head">
            <h2 className="hy-display dbg5-h2">Customize your wall</h2>
          </div>
          <CustomizeWall />
        </section>

        <section className="dbg5-section">
          <div className="dbg5-section-head">
            <h2 className="hy-display dbg5-h2">Embed your wall</h2>
          </div>
          <p className="hy-lead dbg5-embed-copy">
            Paste anywhere — YouTube About page, video descriptions, your own site.
            Updates live as new supporters sign on.
          </p>
          <pre className="dbg5-code hy-mono">{`<iframe src="https://subswall.com/${handle}/embed"
        width="100%" height="600"
        style="border:0;border-radius:14px;"
        loading="lazy"></iframe>`}</pre>
        </section>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Customize wall — theme, background image, font, accent. Loads existing
// settings on mount, PATCHes on change. Anonymous → form is shown but
// shows a "Sign in to customize" notice.
// ---------------------------------------------------------------------------

const FONT_CHOICES = [
  "Bebas Neue", "Inter", "Fraunces", "JetBrains Mono",
  "Playfair Display", "Space Grotesk", "Manrope", "DM Sans",
  "Caveat", "Patrick Hand", "Permanent Marker",
];
const THEME_CHOICES: Array<{ key: "neon"|"chalkboard"|"stickies"; label: string; tag: string }> = [
  { key: "neon",       label: "Neon",       tag: "Streamer billboards" },
  { key: "chalkboard", label: "Chalkboard", tag: "Green board · chalk handwriting" },
  { key: "stickies",   label: "Stickies",   tag: "Cork board · yellow sticky notes" },
];

interface Customization {
  wall_theme: string | null;
  wall_font: string | null;
  wall_accent_color: string | null;
  wall_bg_url: string | null;
  wall_bg_opacity: number | null;
}

function CustomizeWall(): JSX.Element {
  const [c, setC] = useState<Customization>({
    wall_theme: null, wall_font: null, wall_accent_color: null,
    wall_bg_url: null, wall_bg_opacity: null,
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/me/customize", { headers: { Accept: "application/json" } })
      .then((r) => { if (r.status === 401) { setSignedIn(false); return null; } setSignedIn(true); return r.ok ? r.json() : null; })
      .then((j: { customization?: Customization } | null) => {
        if (!cancelled && j?.customization) setC({ ...c, ...j.customization });
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function patch(partial: Partial<Customization>): Promise<void> {
    setSaving(true);
    try {
      await fetch("/api/me/customize", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(partial),
      });
      setC((prev) => ({ ...prev, ...partial }));
    } finally {
      setSaving(false);
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadErr(null);
    try {
      const r = await fetch("/api/me/upload-bg", {
        method: "POST",
        headers: { "Content-Type": f.type },
        body: await f.arrayBuffer(),
      });
      const j = (await r.json()) as { ok?: boolean; url?: string; error?: string };
      if (!j.ok || !j.url) { setUploadErr(j.error ?? "Upload failed"); return; }
      await patch({ wall_bg_url: j.url, wall_bg_opacity: c.wall_bg_opacity ?? 0.55 });
    } catch {
      setUploadErr("Upload failed");
    } finally {
      setUploading(false);
    }
  }

  if (signedIn === false) {
    return (
      <div className="dbg5-cust-card">
        <p className="dbg5-cust-empty">
          Sign in to customize your wall — pick a theme, upload a background,
          choose your font and accent color.
        </p>
      </div>
    );
  }
  if (!loaded) return <div className="dbg5-cust-card"><p>Loading…</p></div>;

  // Five-tile preview at the supporter's current settings so every
  // control change has a live consequence.
  const previewTiles: SubTile[] = [
    { id: "p0", name: "Sarah K.",    message: "first one in",       style: "spray",  color: STYLE_PALETTES.spray[0],  xPct: 25, yPct: 24, monthlyCents: 500,   anniversaryMonths: 0,  founderNumber: null, boostUntil: null, fadeStartedAt: null, badge: null, blink: false },
    { id: "p1", name: "Marcus T.",   message: "$5/mo forever",      style: "chalk",  color: STYLE_PALETTES.chalk[1],  xPct: 70, yPct: 26, monthlyCents: 500,   anniversaryMonths: 0,  founderNumber: null, boostUntil: null, fadeStartedAt: null, badge: null, blink: false },
    { id: "p2", name: "lofi.ghost",  message: "VOD on loop",        style: "neon",   color: STYLE_PALETTES.neon[1],   xPct: 24, yPct: 56, monthlyCents: 1000,  anniversaryMonths: 0,  founderNumber: null, boostUntil: null, fadeStartedAt: null, badge: null, blink: true  },
    { id: "p3", name: "Maya Chen",   message: "since the API days", style: "carved", color: STYLE_PALETTES.carved[2], xPct: 62, yPct: 58, monthlyCents: 2500,  anniversaryMonths: 7,  founderNumber: null, boostUntil: null, fadeStartedAt: null, badge: "shield", blink: false },
    { id: "p4", name: "Helena Voss", message: "day-one founder",    style: "neon",   color: STYLE_PALETTES.neon[0],   xPct: 44, yPct: 84, monthlyCents: 10000, anniversaryMonths: 12, founderNumber: 1,    boostUntil: null, fadeStartedAt: null, badge: "star",   blink: true  },
  ];
  const previewTheme = (c.wall_theme as "neon"|"chalkboard"|"stickies") ?? "neon";

  return (
    <div className="dbg5-cust">
      {/* Live preview — every control below updates this in real time. */}
      <div className="dbg5-cust-preview">
        <div className="dbg5-cust-preview-lbl">Live preview</div>
        <div className="dbg5-cust-preview-frame">
          <SubsWall
            tiles={previewTiles}
            theme={previewTheme}
            autoGrow
            bgImageUrl={c.wall_bg_url}
            bgImageOpacity={c.wall_bg_opacity}
            customFont={c.wall_font}
            customAccent={c.wall_accent_color}
          />
        </div>
      </div>

      {/* Theme picker */}
      <div className="dbg5-cust-row">
        <div className="dbg5-cust-lbl">Theme</div>
        <div className="dbg5-cust-themes">
          {THEME_CHOICES.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`dbg5-cust-theme ${c.wall_theme === t.key ? "dbg5-cust-theme-active" : ""}`}
              onClick={() => patch({ wall_theme: t.key })}
            >
              <div className="dbg5-cust-theme-name">{t.label}</div>
              <div className="dbg5-cust-theme-tag">{t.tag}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Background image */}
      <div className="dbg5-cust-row">
        <div className="dbg5-cust-lbl">Background image</div>
        <div className="dbg5-cust-bg">
          {c.wall_bg_url ? (
            <div className="dbg5-cust-bg-preview" style={{ backgroundImage: `url("${c.wall_bg_url}")` }} />
          ) : <div className="dbg5-cust-bg-empty">No image — theme background shows.</div>}
          <div className="dbg5-cust-bg-acts">
            <label className="dbg5-cust-upload">
              {uploading ? "Uploading…" : c.wall_bg_url ? "Change image" : "Upload image"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFile} hidden />
            </label>
            {c.wall_bg_url ? (
              <button type="button" className="dbg5-cust-clear" onClick={() => patch({ wall_bg_url: null })}>
                Remove
              </button>
            ) : null}
          </div>
          {c.wall_bg_url ? (
            <div className="dbg5-cust-opacity">
              <label>Wallpaper strength
                <input type="range" min="0" max="1" step="0.05"
                       value={c.wall_bg_opacity ?? 0.55}
                       onChange={(e) => patch({ wall_bg_opacity: Number(e.target.value) })} />
                <span className="hy-mono">{Math.round((c.wall_bg_opacity ?? 0.55) * 100)}%</span>
              </label>
            </div>
          ) : null}
          {uploadErr ? <div className="dbg5-cust-err">{uploadErr}</div> : null}
        </div>
      </div>

      {/* Font */}
      <div className="dbg5-cust-row">
        <div className="dbg5-cust-lbl">Font</div>
        <div className="dbg5-cust-fonts">
          {FONT_CHOICES.map((f) => (
            <button
              key={f}
              type="button"
              className={`dbg5-cust-font ${c.wall_font === f ? "dbg5-cust-font-active" : ""}`}
              style={{ fontFamily: `"${f}", system-ui, sans-serif` }}
              onClick={() => patch({ wall_font: c.wall_font === f ? null : f })}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Accent color */}
      <div className="dbg5-cust-row">
        <div className="dbg5-cust-lbl">Accent color</div>
        <div className="dbg5-cust-accent">
          <input
            type="color"
            value={c.wall_accent_color ?? "#5fa8ff"}
            onChange={(e) => patch({ wall_accent_color: e.target.value })}
          />
          <span className="hy-mono">{c.wall_accent_color ?? "#5fa8ff"}</span>
          {c.wall_accent_color ? (
            <button type="button" className="dbg5-cust-clear" onClick={() => patch({ wall_accent_color: null })}>
              Reset
            </button>
          ) : null}
        </div>
      </div>

      {saving ? <div className="dbg5-cust-saving">Saving…</div> : null}
    </div>
  );
}
